import { z } from 'zod'
import {
  AreaCodeSchema,
  ContractStatusSchema,
  OcrProviderSchema,
  PremiumFrequencySchema,
} from '../enums.js'

// Diagnostic payload returned by `POST /api/ocr/extract` next to `ocr`.
// Lets the /ocr-test page show whether Claude actually ran (vs. silently
// falling back to mock), why a fallback happened, what Claude emitted before
// the Zod parse, and end-to-end latency. Not consumed by any other endpoint.
export const OcrDebugSchema = z.object({
  // Provider the env asked us to try first.
  attempted_provider: OcrProviderSchema,
  // Provider that actually produced the returned result. Differs from
  // `attempted_provider` when the Claude call fell back to the mock fixture.
  effective_provider: OcrProviderSchema,
  // Categorised reason for the fallback. Mirrors the labels in
  // api/src/ocr/claude-vision.ts (`auth`, `rate_limit`, `timeout`,
  // `upstream_5xx`, `schema_mismatch`, `no_tool_use`, `api_<status>`,
  // `unknown`). Present iff effective !== attempted.
  fallback_reason: z.string().optional(),
  duration_ms: z.number().nonnegative(),
  // The `tool_use.input` Claude emitted, before our Czech-date normaliser and
  // before Zod parsing. Set on Claude success AND on schema-mismatch
  // fallbacks — both cases want it visible during prompt iteration.
  raw_tool_input: z.unknown().optional(),
  // Set iff Zod rejected the (normalised) Claude output.
  zod_issues: z
    .array(
      z.object({
        path: z.array(z.union([z.string(), z.number()])),
        message: z.string(),
        code: z.string(),
      }),
    )
    .optional(),
  // Set iff the Anthropic SDK raised an APIError.
  api_error: z
    .object({
      status: z.number().optional(),
      type: z.string().optional(),
      message: z.string(),
    })
    .optional(),
  // Uploaded file size + mime — handy when debugging "OCR is bad" reports
  // ("oh, it was 200 KiB and webp, no wonder").
  file: z
    .object({
      bytes: z.number().nonnegative(),
      mime_type: z.string().optional(),
    })
    .optional(),
})
export type OcrDebug = z.infer<typeof OcrDebugSchema>

export const OcrResultSchema = z.object({
  contract_id: z.string().min(1),
  insurer: z.string().min(1),
  type: AreaCodeSchema,
  // Empty is a legitimate pre-resolution state: the prompt (see
  // api/src/ocr/claude-vision.prompt.ts) instructs Claude to return "" when
  // the holder isn't a known family member, and have `holder_extracted`
  // populated instead. The upload route then creates the member and
  // rewrites this to a real id. ContractSchema stays strict (.min(1)).
  holder_member_id: z.string(),
  insured_person_ids: z.array(z.string().min(1)),
  coverages: z.array(
    z.object({
      area: AreaCodeSchema,
      sum_insured_czk: z.number().nonnegative(),
      deductible_czk: z.number().nonnegative().nullable().optional(),
    }),
  ),
  premium_czk: z.number().nonnegative(),
  valid_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  valid_to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  contract_number: z.string().min(1),
  premium_frequency: PremiumFrequencySchema.optional(),
  auto_renewal: z.boolean().nullable().optional(),
  status: ContractStatusSchema,
  holder_extracted: z
    .object({
      name: z.string().min(1),
      dob: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      email: z.string().email().optional(),
      address: z.string().min(1).optional(),
    })
    .optional(),
})
export type OcrResult = z.infer<typeof OcrResultSchema>

// Response body of `POST /api/ocr/extract`. Carries the result and the
// debug payload side-by-side so the /ocr-test page can render both without
// a second round-trip. The persistent `/api/contracts/upload` route does
// NOT use this shape — it still returns the regular Contract envelope.
export const OcrExtractResponseSchema = z.object({
  ocr: OcrResultSchema,
  debug: OcrDebugSchema,
})
export type OcrExtractResponse = z.infer<typeof OcrExtractResponseSchema>
